import { BASE_URL } from './baseURL';

// API endpoints
const ENDPOINTS = {
  IPA_QUOTE: '/ipa/quote',
};

// Generic API call function with error handling
const makeAPICall = async (endpoint, data, token, options = {}) => {
  const { timeout = 30000 } = options; // 30 second timeout

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    console.log(`=== Making Personal Accident API call ===`);
    console.log(`Endpoint: ${BASE_URL}${endpoint}`);
    console.log(`Payload:`, JSON.stringify(data, null, 2));
    console.log(`Timeout: ${timeout}ms`);

    const headers = {
      'accept': 'application/json',
      'Content-Type': 'application/json',
    };

    // Only add Authorization header if token is provided (for logged-in users)
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const startTime = Date.now();
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const endTime = Date.now();
    console.log(`API call completed in ${endTime - startTime}ms`);
    console.log(`Response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API Error Response:`, errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const responseData = await response.json();
    console.log(`=== API Response Data ===`);
    console.log(JSON.stringify(responseData, null, 2));

    return responseData;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      console.error(`API call timed out after ${timeout}ms`);
      throw new Error(`Request timeout after ${timeout}ms`);
    }

    console.error(`API call failed:`, error);
    throw error;
  }
};

// Transform API response to camelCase nested structure
const transformQuoteResponse = (apiResponse) => {
  if (!apiResponse) return null;

  console.log('=== Transforming Personal Accident Quote Response ===');
  console.log('Original API response:', apiResponse);

  const transformed = {
    success: true,
    data: {
      policyNumber: apiResponse.PolicyNumber || null,
      quoteNumber: apiResponse.QuoteDetails?.QuoteNumber || apiResponse.PolicyNumber || null,
      sumInsured: parseFloat(apiResponse.SumInsured || 0),
      totalPremium: parseFloat(apiResponse.TotalPremium || 0),
      premium: parseFloat(apiResponse.Premium || 0),
      tax: parseFloat(apiResponse.Tax || 0),
      fromDate: apiResponse.QuoteDetails?.FromDateStr || null,
      toDate: apiResponse.QuoteDetails?.ToDateStr || null,
      productName: apiResponse.QuoteDetails?.ProductName || 'Individual Personal Accident',
      productCode: apiResponse.QuoteDetails?.ProductCode || '0202',
      partyCode: apiResponse.QuoteDetails?.PartyCode || null,
    },
    raw: apiResponse // Keep original response for reference
  };

  console.log('Transformed response:', transformed);
  return transformed;
};

/**
 * Get Personal Accident Insurance Quote
 * @param {Object} params - Quote parameters
 * @param {number} params.age - User age
 * @param {number} params.occupation - Occupation code
 * @param {string} params.partyCode - Party code from checkParty endpoint (default FALCON for guests)
 * @param {string} params.sumInsured - Sum insured amount
 * @param {string} params.token - Authorization bearer token (optional for guests)
 * @returns {Promise<Object>} - Transformed quote response
 */
export const getPersonalAccidentQuote = async ({ age, occupation, partyCode = 'FALCON', sumInsured, token }) => {
  try {
    console.log('=== Getting Personal Accident Quote ===');
    console.log('Parameters:', { age, occupation, partyCode, sumInsured });

    // Validate required parameters
    if (age === undefined || age === null || age === '') throw new Error('Age is required');
    if (!occupation) throw new Error('Occupation is required');
    if (!sumInsured) throw new Error('SumInsured is required');
    // Note: partyCode defaults to 'FALCON' for guests
    // Note: token may be optional for guest quotes depending on API requirements

    const payload = {
      Age: parseInt(age),
      Channel: 'online.portal',
      Occupation: parseInt(occupation),
      PartyCode: partyCode,
      SumInsured: sumInsured.toString(),
    };

    // Note: personal accident quote payload does not carry a CPR - keep
    // FALCON as the guest fallback for early calls.

    const response = await makeAPICall(ENDPOINTS.IPA_QUOTE, payload, token);

    // Transform the response to match our standard format
    return transformQuoteResponse(response);
  } catch (error) {
    console.error('Get Personal Accident Quote Error:', error);
    throw error;
  }
};

export default {
  getPersonalAccidentQuote,
};
